import { themeFontWeights } from "@dana/MobiusOverrides/styles/variables";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    discountTotal?: number;
    extendedStyles?: CartTotalDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    annualReturnsAllowance: state.context.session?.properties?.annualReturnsAllowance || "0.00",
    annualReturnsAllowanceDisplay: state.context.session?.properties?.annualReturnsAllowanceDisplay || "$0.00",
    annualReturnsBalance: (state as ApplicationStateDana)?.annualReturnsOrderState?.balance ?? 0.00,
    annualReturnsBalanceDisplay: (state as ApplicationStateDana)?.annualReturnsOrderState?.balanceDisplay ?? "$0.00",
    isAnnualReturnsOrder: getCookie("cartMode") === "AnnualReturnsOrderCart",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
}

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: ${getColor("common.accent")};
            padding: 20px;
        `,
    },
    labelGridItem: {
        width: 6,
        printWidth: 6,

    },
    valueGridItem: {
        width: 6,
        printWidth: 6,
    },
    subtotalLabel: {
        weight: themeFontWeights?.semiBold
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    promotionLabel: {
        weight: themeFontWeights?.semiBold
    },
    promotionValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsLabel: {
        weight: themeFontWeights?.semiBold
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingLabel: {
        weight: themeFontWeights?.semiBold
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesLabel: {
        weight: themeFontWeights?.semiBold
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxLabel: {
        weight: themeFontWeights?.semiBold
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabel: { weight: themeFontWeights?.semiBold },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
};

const SavedOrderDetailsCartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    isLoading,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    annualReturnsAllowance,
    annualReturnsBalance,
    annualReturnsBalanceDisplay,
    annualReturnsAllowanceDisplay,
    isAnnualReturnsOrder,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));

    const [filteredOrderPromotions, setFilteredOrderPromotions] = React.useState<PromotionModel[] | undefined>(
        () => orderPromotions?.filter(o => o.amount > 0)
    );
    const [filteredShippingPromotions, setFilteredShippingPromotions] = React.useState<PromotionModel[] | undefined>(
        () => shippingPromotions?.filter(o => o.amount > 0)
    );
    const [totalQuantity, setTotalQuantity] = React.useState<number | undefined>(
        () => cart?.cartLines?.reduce((sum, line) => sum + (line.qtyOrdered || 0), 0) || 0
    );
    const [totalWeight, setTotalWeight] = React.useState<string | undefined>(
        () => cart?.properties?.totalWeight
    );
    const [linesCount, setLinesCount] = React.useState<number | undefined>(
        () => cart?.cartLines?.length
    );

    React.useEffect(() => {
        const newTotalQuantity = cart?.cartLines?.reduce((sum, line) => sum + (line.qtyOrdered || 0), 0) || 0;
        const newTotalWeight = cart?.properties?.totalWeight;
        const filteredOrderPromotionsNew = orderPromotions?.filter(o => o.amount > 0);
        const filteredShippingPromotionsNew = shippingPromotions?.filter(o => o.amount > 0);
        setTotalQuantity(newTotalQuantity);
        setTotalWeight(newTotalWeight!);
        setFilteredOrderPromotions(filteredOrderPromotionsNew);
        setFilteredShippingPromotions(filteredShippingPromotionsNew);
        setLinesCount(cart?.cartLines?.length)
    }, [cart, cart?.cartLines, orderPromotions, shippingPromotions]);

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    if (isLoading || !cart) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>
                                {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    if (isCartEmpty) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>
                                {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    return (
        <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel}>
                    {translate("No. of Line(s)")}
                </Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_cartlines">
                    {linesCount}
                </Typography>
            </GridItem>
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel}>
                    {translate("Total Quantity")}
                </Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_totalQty">
                    {totalQuantity}
                </Typography>
            </GridItem>
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel}>
                    {translate("Total Weight (LB)")}
                </Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_totalWeight">
                    {totalWeight !== undefined ? totalWeight : 0}
                </Typography>
            </GridItem>
            {isAnnualReturnsOrder && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Initial Allowance")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_allowance">
                            {annualReturnsAllowanceDisplay}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Remaining Balance")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_balance">
                            {annualReturnsBalanceDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {cart.orderSubTotal !== cart.orderGrandTotal && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>
                            {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                            {cart.orderSubTotalDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {filteredOrderPromotions &&
                filteredOrderPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel} data-test-selector="cartTotal_orderPromotionName">
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.promotionValue} data-test-selector="cartTotal_orderPromotionValue">
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.shippingAndHandling > 0 && cart.shippingAndHandlingDisplay.length > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel}>{translate("Shipping & Handling")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.shippingAndHandlingValue}
                            data-test-selector="cartTotal_shippingAndHandlingAmount"
                        >
                            {cart.shippingAndHandlingDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {filteredShippingPromotions &&
                filteredShippingPromotions.map(promotion => (
                    <Fragment key={promotion.id}>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.promotionLabel}>
                                {`${translate("Promotion")}: ${promotion.name}`}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography
                                {...styles.promotionValue}
                                data-test-selector="cartTotal_shippingPromotionValue"
                            >
                                {`-${promotion.amountDisplay}`}
                            </Typography>
                        </GridItem>
                    </Fragment>
                ))}
            {showTaxAndShipping && cart.totalTax > 0 && cart.totalTaxDisplay.length > 0 && cart.customerOrderTaxes!.length === 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                            {cart.totalTaxDisplay}
                        </Typography>
                    </GridItem>
                </>
            )}
            {showTaxAndShipping && cart.customerOrderTaxes &&
                [...cart.customerOrderTaxes!]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography>{tax.taxDescription || translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                </GridItem>
                            </Fragment>
                        );
                    })}

            {/* <>
                <GridItem {...styles.labelGridItem}>
                    <Typography {...styles.totalLabel}>{translate("Order Total")}</Typography>
                </GridItem>
                <GridItem {...styles.valueGridItem}>
                    <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                        {cart.orderGrandTotalDisplay}
                    </Typography>
                </GridItem>
            </> */}

            {/* {discountTotal !== undefined && discountTotal > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.discountsValue} data-test-selector="cartTotal_totalDiscounts">
                            {`${translate("You saved")} `}{" "}
                            <LocalizedCurrency amount={discountTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            )} */}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(SavedOrderDetailsCartTotalDisplay);
